/**
 * Session 3C — shared shell + primitives for the 27 bespoke policy pages.
 *
 * Per §3.1 of the prompt — every policy page has:
 *   - PageHeader (Raleway display title · subtitle · actions row)
 *   - 1-2 InlineAISuggestion cards (Mutaba'aa-led)
 *   - DataTable (rules list) | AiBriefPanel (Mutaba'aa narrating compliance)
 *
 * Exports:
 *   <Session3cPolicyShell />     — the grid 1fr + 280px layout
 *   <PolicyActionsRow />         — [+ New] [✦ Import from PDF] [✦ Suggest defaults] [Export]
 *   <MutabaaVetoModal />         — save-time blocking modal when floor breached
 *   <OverrideStackPopover />     — TraceableValue popover content
 *   <M19PendingDraftsBanner />   — AI-tinted banner when domain has pending drafts
 *   <SuggestDefaultButton />     — ✦ Suggest button next to numeric inputs
 */

import { AlertOctagon, Download, Plus, Sparkles, Upload, X } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { AiBriefPanel } from "../shell/AiBriefPanel";
import { PageHeader } from "../components/ui";
import type { MutabaaFloorAssessment, PolicyValueWithStack } from "../lib/session3c-api";

// ---------------------------------------------------------------------------
// Session3cPolicyShell — the locked layout
// ---------------------------------------------------------------------------

export type Session3cAgent = "tashghil" | "rawatib" | "mutabaa" | "khidmat";

export interface Session3cShellProps {
  title: string;
  subtitle: string;
  agent: Session3cAgent;
  briefContext: {
    total: number;
    attention: number;
    critical: number;
    top_kind: string;
    window_label: string;
  };
  actionsRow?: ReactNode;
  children: ReactNode;
}

export function Session3cPolicyShell(props: Session3cShellProps) {
  const { title, subtitle, agent, briefContext, actionsRow, children } = props;
  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader title={title} subtitle={subtitle} />
        {actionsRow}
        {children}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel agent={agent} liveContext={briefContext} />
      </aside>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PolicyActionsRow — [+ New] [✦ Import] [✦ Suggest] [Export]
// ---------------------------------------------------------------------------

export interface PolicyActionsRowProps {
  /** Domain key for M19 ingestion routing (e.g., 'leave_accrual'). */
  domain: string;
  newLabel: string;
  onNewClick: () => void;
  onExportClick?: () => void;
  pendingDraftsCount?: number;
  /** Optional left-aligned hint text. */
  hint?: string;
}

export function PolicyActionsRow(props: PolicyActionsRowProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { domain, newLabel, onNewClick, onExportClick, pendingDraftsCount = 0, hint } = props;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="text-xs text-text-tertiary">{hint ?? ""}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        <button
          type="button"
          onClick={() => navigate(`/policies/ai-ingestion?domain=${encodeURIComponent(domain)}`)}
          className="inline-flex items-center gap-1 rounded border border-[#e9d5ff] bg-[#f5f3ff] px-2.5 py-1.5 text-xs font-medium text-[#6b21a8] hover:bg-[#ede9fe]"
        >
          <Upload className="h-3 w-3" />
          <span aria-hidden="true">✦</span>
          {t("policies.actions.import_pdf", "Import from PDF")}
          {pendingDraftsCount > 0 && (
            <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-purple-600 px-1 text-[9px] font-semibold text-white">
              {pendingDraftsCount}
            </span>
          )}
        </button>
        {onExportClick && (
          <button
            type="button"
            onClick={onExportClick}
            className="inline-flex items-center gap-1 rounded border border-border-subtle bg-white px-2.5 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-sunken"
          >
            <Download className="h-3 w-3" />
            {t("policies.actions.export", "Export")}
          </button>
        )}
        <button
          type="button"
          onClick={onNewClick}
          className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
        >
          <Plus className="h-4 w-4" />
          {newLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MutabaaVetoModal — Solvait-branded blocking modal when floor is breached
// ---------------------------------------------------------------------------

export interface MutabaaVetoModalProps {
  assessment: MutabaaFloorAssessment;
  attemptedValue: number;
  onClose: () => void;
  onAutoFixToFloor?: () => void;
}

export function MutabaaVetoModal(props: MutabaaVetoModalProps) {
  const { t } = useTranslation();
  const { assessment, attemptedValue, onClose, onAutoFixToFloor } = props;
  if (!assessment.blocked) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t("policies.veto.aria_label", "Mutaba'aa statutory floor veto")}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-w-lg rounded-2xl border-2 border-purple-200 bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-purple-800">
            <AlertOctagon className="h-4 w-4" />
            <span>{t("policies.veto.title", "Mutaba'aa statutory floor veto")}</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
            className="rounded p-1 text-text-tertiary hover:bg-surface-sunken"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-3 text-sm font-medium text-text-primary">
          {assessment.article_citation ?? "—"}
        </p>
        {assessment.article_summary && (
          <p className="mt-1 text-xs text-text-secondary">{assessment.article_summary}</p>
        )}
        <div className="mt-4 grid grid-cols-2 gap-3 rounded-lg bg-surface-sunken p-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
              {t("policies.veto.attempted", "Attempted value")}
            </div>
            <div className="font-mono text-base font-semibold text-rose-700">
              {attemptedValue}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-text-tertiary">
              {t("policies.veto.floor", "Statutory floor")}
            </div>
            <div className="font-mono text-base font-semibold text-emerald-700">
              {assessment.floor_value ?? "—"}
            </div>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-text-tertiary">
          {t(
            "policies.veto.mode_invariant_note",
            "Save blocked mode-invariantly per Commandment 45 — even Aggressive autonomy cannot relax a statutory floor.",
          )}
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border-subtle px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-sunken"
          >
            {t("common.cancel", "Cancel")}
          </button>
          {onAutoFixToFloor && assessment.floor_value !== null && (
            <button
              type="button"
              onClick={onAutoFixToFloor}
              className="inline-flex items-center gap-1 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
            >
              {t("policies.veto.auto_fix", "Use the floor")} ({assessment.floor_value})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// OverrideStackPopover — what TraceableValue.onTraceClick passes via the host
// ---------------------------------------------------------------------------

/**
 * Build the override-stack explanation string consumed by
 * `showAlertTrace` → `<TraceExplainerHost />`. Mirrors §3.3 of the
 * Session 3c prompt — KSA floor → CustomerGroup → LE → ACTIVE.
 */
export function buildOverrideStackTrace(
  label: string,
  stack: PolicyValueWithStack,
): string {
  const lines: string[] = [`${label} · active value: ${stack.value}`, ""];
  if (stack.ksa_floor !== null) {
    const floorMark = stack.active_layer === "floor" ? " ◀ ACTIVE" : "";
    lines.push(
      `① KSA Statutory Floor (${stack.ksa_article ?? "—"}): ${stack.ksa_floor}${floorMark}`,
    );
  }
  if (stack.group_default !== null) {
    const groupMark = stack.active_layer === "group" ? " ◀ ACTIVE" : "";
    lines.push(`② CustomerGroup Default: ${stack.group_default}${groupMark}`);
  } else {
    lines.push("② CustomerGroup Default: — (not set)");
  }
  if (stack.le_override !== null) {
    const leMark = stack.active_layer === "le" ? " ◀ ACTIVE" : "";
    lines.push(`③ This LE Override: ${stack.le_override}${leMark}`);
  } else {
    lines.push("③ This LE Override: — (not set)");
  }
  if (stack.last_changed_actor || stack.last_changed_at) {
    lines.push("");
    lines.push(
      `Last changed: ${stack.last_changed_at ?? "—"} by ${stack.last_changed_actor ?? "—"}`,
    );
    if (stack.last_change_note) {
      lines.push(`Note: "${stack.last_change_note}"`);
    }
  }
  return lines.join("\n");
}

// ---------------------------------------------------------------------------
// M19PendingDraftsBanner
// ---------------------------------------------------------------------------

export interface M19PendingDraftsBannerProps {
  domain: string;
  count: number;
  sourceFile?: string;
}

export function M19PendingDraftsBanner(props: M19PendingDraftsBannerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { domain, count, sourceFile } = props;
  if (count === 0) return null;
  return (
    <div className="rounded-lg border border-[#e9d5ff] bg-[#f5f3ff] px-3 py-2 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1">
          <div className="font-medium text-[#6b21a8]">
            <span aria-hidden="true">✦ </span>
            {t(
              "policies.m19_banner.headline",
              `${count} ${domain} rule${count === 1 ? "" : "s"} pending HR review`,
            )}
          </div>
          {sourceFile && (
            <div className="mt-0.5 text-[10px] text-purple-900/70">
              {t("policies.m19_banner.source", `Extracted from ${sourceFile}`)}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => navigate(`/policies/ai-ingestion?domain=${encodeURIComponent(domain)}`)}
          className="rounded border border-purple-400 bg-white px-2 py-1 text-[10px] font-medium text-purple-800 hover:bg-purple-50"
        >
          {t("policies.m19_banner.review", "Review drafts")}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SuggestDefaultButton — ✦ Suggest next to numeric inputs
// ---------------------------------------------------------------------------

export interface SuggestDefaultButtonProps {
  policyKey: string;
  onSuggest: (value: number, rationale: string) => void;
  disabled?: boolean;
}

export function SuggestDefaultButton(props: SuggestDefaultButtonProps) {
  const { t } = useTranslation();
  const { policyKey, onSuggest, disabled } = props;
  const handle = () => {
    // POST /api/ai-assist/suggest-policy-default isn't live yet; the
    // helper here calls the deterministic fallback path the server
    // would use too. When the endpoint ships, replace with a fetch.
    const det = _deterministicDefault(policyKey);
    onSuggest(det.value, det.rationale);
  };
  return (
    <button
      type="button"
      onClick={handle}
      disabled={disabled}
      className="inline-flex items-center gap-1 rounded border border-[#e9d5ff] bg-[#f5f3ff] px-2 py-1 text-[10px] font-medium text-[#6b21a8] hover:bg-[#ede9fe] disabled:opacity-50"
    >
      <Sparkles className="h-3 w-3" />
      {t("policies.suggest_default", "Suggest default")}
    </button>
  );
}

/**
 * Deterministic fallback for ai_assist/suggest-policy-default —
 * matches the server-side default the future endpoint will emit when
 * no Anthropic key is configured. Keys come from §3.6 of the prompt.
 */
function _deterministicDefault(key: string): { value: number; rationale: string } {
  const map: Record<string, { value: number; rationale: string }> = {
    "annual_leave_days_year_1_4": {
      value: 21,
      rationale: "KSA Article 109 minimum for first 5 years of service.",
    },
    "annual_leave_days_year_5_plus": {
      value: 30,
      rationale: "KSA Article 109 minimum from year 5 onward.",
    },
    "sick_leave_full_pay_days": {
      value: 30,
      rationale: "KSA Article 117 — first tier full pay.",
    },
    "ot_multiplier_weekday": {
      value: 1.5,
      rationale: "KSA Article 107 — 150% of basic hourly rate.",
    },
    "ot_multiplier_weekend": {
      value: 1.5,
      rationale: "KSA Article 107 — 150% applies to all overtime worked.",
    },
    "ot_multiplier_holiday": {
      value: 1.5,
      rationale: "KSA Article 107 — 150% applies to all overtime worked.",
    },
    "article_90_cap_pct": {
      value: 33.33,
      rationale: "KSA Article 90 — 1/3 wage deduction cap (cannot be raised).",
    },
    "notice_period_days_indefinite": {
      value: 30,
      rationale: "KSA Article 75 — 30-day minimum for indefinite contracts.",
    },
    "eos_multiplier_year_1_5": {
      value: 0.5,
      rationale: "KSA Article 84/85 — half month per year for first 5 years.",
    },
    "eos_multiplier_year_5_plus": {
      value: 1.0,
      rationale: "KSA Article 84/85 — one month per year from year 5.",
    },
    "travel_advance_pct": {
      value: 70,
      rationale: "M3X.TRAVEL convention — 70% upfront, 30% on reconciliation.",
    },
  };
  return (
    map[key] ?? {
      value: 0,
      rationale: "No deterministic default — set based on internal policy.",
    }
  );
}
